import { Button, Card, Flex, Form, InputNumber, message, Space } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { BoosterMilestoneDetail, BoosterMilestoneFormValues, BoosterRewardType } from 'Modules'
import { useState } from 'react'

import { BOOSTER_PROGRAM_FIELD_MAX_LENGTH } from 'constants/booster-program'

import { MilestoneFieldsBlock } from '../form-create/milestone-fields-block'
import { getThresholdRules, isFormValidateError } from '../form-create/utils'

const MAX = BOOSTER_PROGRAM_FIELD_MAX_LENGTH

interface Props {
  milestone: BoosterMilestoneDetail
  index: number
  /** Returns the previous milestone's threshold for E07 (Buy > prev) check. */
  getPreviousThreshold?: () => number | undefined
  /** Returns the next milestone's threshold for E08 (Buy < next) check. */
  getNextThreshold?: () => number | undefined
  onCancel: () => void
  onSave: (values: BoosterMilestoneFormValues) => Promise<void> | void
}

const toFormValues = (m: BoosterMilestoneDetail): BoosterMilestoneFormValues => ({
  threshold: m.threshold,
  rewardType: m.rewardType,
  discountValue: m.discountValue,
  boostAprValue: m.boostAprValue,
  cashbackRate: m.cashbackRate,
  bonusBuy: m.bonusBuy,
  bonusGet: m.bonusGet,
  tokenType: m.tokenType,
  totalSupply: m.totalSupply,
  codeExpiry: m.codeExpiry,
  ptoSoldOut: m.ptoSoldOut,
  linkedPtos: m.linkedPtos.map((p) => p.ptoId),
})

export const MilestoneEditForm = ({
  milestone,
  index,
  getPreviousThreshold,
  getNextThreshold,
  onCancel,
  onSave,
}: Props) => {
  const [form] = Form.useForm<BoosterMilestoneFormValues>()
  const [saving, setSaving] = useState(false)
  const rewardType = useWatch('rewardType', form) as BoosterRewardType | undefined

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)
      await onSave(values)
    } catch (err) {
      if (isFormValidateError(err)) return
      message.error('Failed to save milestone')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="booster-program-create__milestone-card">
      <Form
        form={form}
        layout="vertical"
        variant="filled"
        className="booster-program-create"
        initialValues={toFormValues(milestone)}
      >
        <div className="booster-program-create__milestone-header">
          <div className="booster-program-create__milestone-heading">
            <span className="booster-program-create__milestone-label">M{index + 1} —</span>
            <span>Buy ≥</span>
            <Form.Item
              name="threshold"
              rules={getThresholdRules(index, getPreviousThreshold, getNextThreshold)}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                maxLength={MAX.purchaseQuantity}
                placeholder="Input number"
                style={{ width: 100 }}
              />
            </Form.Item>
            <span className="booster-program-create__milestone-label">PTO (cumulative)</span>
          </div>
        </div>

        <MilestoneFieldsBlock rewardType={rewardType} />

        <Flex justify="flex-end" gap={8} className="booster-detail__edit-actions">
          <Space>
            <Button onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              className="booster-program-create__primary-btn"
              loading={saving}
              onClick={handleSave}
            >
              Save
            </Button>
          </Space>
        </Flex>
      </Form>
    </Card>
  )
}
